import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '@/app/providers/AuthProvider';
import { useTheme } from '@/app/providers/ThemeProvider';
import { useAdminEvent } from './AdminEventContext';
import { EventSelector } from '@/features/admin/components/EventSelector';
import './AdminShell.css';

const NAV_ITEMS = [
  { to: '/admin/events', label: 'Events', icon: '◧' },
  { to: '/admin/exhibitors', label: 'Exhibitors', icon: '▦' },
  { to: '/admin/delegates', label: 'Delegates', icon: '☰' },
  { to: '/admin/users', label: 'Users', icon: '◎' },
  { to: '/admin/analytics', label: 'Analytics', icon: '▤' }
] as const;

export function AdminShell({ title, children }: { title: string; children: ReactNode }) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { events, eventId, setEventId } = useAdminEvent();

  return (
    <div className="admin-shell">
      <aside className="admin-shell__sidebar">
        <img src="/brand/logo.png" alt="The Foot & Ankle Show" className="admin-shell__brand" />
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) => `admin-shell__nav-item${isActive ? ' admin-shell__nav-item--active' : ''}`}
          >
            <span aria-hidden="true">{item.icon}</span>
            {item.label}
          </NavLink>
        ))}
      </aside>

      <div className="admin-shell__main">
        <header className="admin-shell__topbar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
            <h1 className="font-display" style={{ fontSize: '1.25rem', margin: 0 }}>
              {title}
            </h1>
            <EventSelector events={events} eventId={eventId} onChange={setEventId} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
            <button
              type="button"
              onClick={toggleTheme}
              aria-label="Toggle theme"
              style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1.125rem' }}
            >
              {theme === 'dark' ? '☀' : '☾'}
            </button>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
              {user?.firstName} {user?.lastName}
            </span>
            <button
              type="button"
              onClick={() => void logout()}
              style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.875rem' }}
            >
              Log out
            </button>
          </div>
        </header>

        <div className="admin-shell__content">{children}</div>
      </div>
    </div>
  );
}
